import { Fragment, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  KeyRound,
  LifeBuoy,
  Loader2,
  Plus,
  RefreshCw,
  Ticket,
  Users,
  XCircle,
} from "lucide-react";
import { useLinkLightAdminAuth } from "../../context/LinkLightAdminAuthContext";
import {
  createLicenseCredential,
  createSupportTicket,
  listAllTenantLicenses,
  listLicenseCredentials,
  listLoginActivity,
  listSupportTickets,
  reissueLicenseCredential,
  setLinkLightAdminContext,
  updateLicenseCredentialStatus,
} from "../../services/compliance-api";
import type {
  ComplianceRole,
  LicenseCredentialItem,
  LoginActivityItem,
  SupportTicketItem,
} from "../../types/compliance";

const ROLES: { value: ComplianceRole; label: string }[] = [
  { value: "COMPLIANCE_ADMIN", label: "Compliance Admin" },
  { value: "FINANCE_REVIEWER", label: "Finance Reviewer" },
  { value: "SHIPMENT_OPERATOR", label: "Shipment Operator" },
  { value: "AUDITOR", label: "Auditor" },
];

function formatDate(value?: string | null) {
  if (!value) return "No expiry";
  return new Date(value).toLocaleString();
}

function daysUntil(value?: string | null) {
  if (!value) return null;
  return Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000);
}

type LicenseRow = LicenseCredentialItem & { tenant_client_name?: string | null };

export default function AdminLicenseConsole() {
  const { session } = useLinkLightAdminAuth();

  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [activity, setActivity] = useState<LoginActivityItem[]>([]);
  const [tickets, setTickets] = useState<SupportTicketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLicenseKey, setNewLicenseKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  // Empty filter = show every tenant's licenses at once (the default view).
  // Typing a tenant ID and pressing "Load Tenant" narrows to just that tenant.
  const [tenantFilter, setTenantFilter] = useState("");
  const [viewingTenantId, setViewingTenantId] = useState<string | null>(null);
  const [expandedLicenseId, setExpandedLicenseId] = useState<string | null>(null);

  const [clientTenantId, setClientTenantId] = useState("tenant_client");
  const [clientName, setClientName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ComplianceRole>("AUDITOR");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");

  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");

  const metrics = useMemo(() => {
    const active = licenses.filter((item) => item.status === "active").length;
    const expiring = licenses.filter((item) => {
      const days = daysUntil(item.expires_at);
      return days !== null && days >= 0 && days <= 14;
    }).length;
    const requests = licenses.reduce((sum, item) => sum + item.request_count, 0);
    return { active, expiring, requests };
  }, [licenses]);

  async function load() {
    if (!session) return;
    setLinkLightAdminContext(session.tenantId, session.licenseKey);
    setLoading(true);
    setError(null);
    const targetTenant = tenantFilter.trim();
    try {
      if (!targetTenant) {
        // Default view: every tenant that has ever had a license issued,
        // no manual tenant lookup required.
        const portfolio = await listAllTenantLicenses();
        setLicenses(
          portfolio.tenants.flatMap((group) =>
            group.licenses.map((item) => ({
              ...item,
              tenant_client_name: group.client_name,
            })),
          ),
        );
        setActivity([]);
        setTickets([]);
        setViewingTenantId(null);
      } else {
        const [licenseRes, activityRes, ticketRes] = await Promise.all([
          listLicenseCredentials(targetTenant),
          listLoginActivity(targetTenant),
          listSupportTickets(targetTenant),
        ]);
        setLicenses(licenseRes.items);
        setActivity(activityRes.items);
        setTickets(ticketRes.items);
        setViewingTenantId(targetTenant);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load admin console");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [session?.tenantId, tenantFilter]);

  async function handleCreateLicense() {
    if (!session) return;
    setCreating(true);
    setError(null);
    setNewLicenseKey(null);
    setKeyCopied(false);
    try {
      const response = await createLicenseCredential({
        tenant_id: clientTenantId.trim(),
        client_name: clientName.trim(),
        contact_email: email.trim(),
        role,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        notes: notes.trim() || null,
      });
      setLicenses((prev) => [response.credential, ...prev]);
      setNewLicenseKey(response.license_key);
      setTenantFilter(clientTenantId.trim());
      setClientName("");
      setEmail("");
      setRole("AUDITOR");
      setExpiresAt("");
      setNotes("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create license");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(licenseId: string, tenantId: string) {
    try {
      const updated = await updateLicenseCredentialStatus(licenseId, "revoked", tenantId);
      setLicenses((prev) =>
        prev.map((item) => (item.license_id === licenseId ? updated : item)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke license");
    }
  }

  async function handleReissue(licenseId: string, tenantId: string) {
    setError(null);
    setNewLicenseKey(null);
    setKeyCopied(false);
    try {
      const response = await reissueLicenseCredential(licenseId, tenantId);
      setLicenses((prev) => [
        response.credential,
        ...prev.map((item) =>
          item.license_id === licenseId ? { ...item, status: "revoked" } : item,
        ),
      ]);
      setNewLicenseKey(response.license_key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reissue license");
    }
  }

  async function handleCreateTicket() {
    if (!session) return;
    try {
      const ticket = await createSupportTicket({
        tenant_id: tenantFilter,
        subject: ticketSubject.trim(),
        description: ticketDescription.trim(),
        priority: "medium",
        created_by_email: "root-admin@linklight.ai",
      });
      setTickets((prev) => [ticket, ...prev]);
      setTicketSubject("");
      setTicketDescription("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create ticket");
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50 p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
            LightLink AI Admin
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950">
            License Credential Portfolio
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Generate client license keys, control expiry, track logins, and manage support.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-blue-700">
          {viewingTenantId
            ? `Filtered to ${viewingTenantId}`
            : "All tenants (showing every client's licenses)"}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            placeholder="Leave empty to see every tenant, or type a tenant ID to narrow down"
            className="flex-1 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-slate-900"
          />
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            {tenantFilter.trim() ? "Load Tenant" : "Show All Tenants"}
          </button>
          {viewingTenantId && (
            <button
              type="button"
              onClick={() => {
                setTenantFilter("");
                void load();
              }}
              className="rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="mb-6 grid gap-3 md:grid-cols-4">
        {[
          { label: "Active licenses", value: metrics.active, Icon: KeyRound },
          { label: "Expiring in 14 days", value: metrics.expiring, Icon: Clock },
          { label: "Usage requests", value: metrics.requests, Icon: Users },
          { label: "Support tickets", value: tickets.length, Icon: LifeBuoy },
        ].map(({ label, value, Icon }) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <Icon size={18} className="mb-3 text-blue-600" />
            <p className="text-2xl font-bold text-slate-950">{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Plus size={16} />
            Generate Client License
          </h2>
          <div className="grid gap-3">
            <input
              value={clientTenantId}
              onChange={(e) => setClientTenantId(e.target.value)}
              placeholder="Client tenant ID, e.g. tenant_acme_logistics"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client or company name"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client.user@company.com"
              type="email"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as ComplianceRole)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                {ROLES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <input
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                type="datetime-local"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes"
              rows={3}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void handleCreateLicense()}
              disabled={creating || !clientTenantId || !clientName || !email}
              className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Generate License Key
            </button>
          </div>
          {newLicenseKey && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-emerald-800">
                <CheckCircle2 size={15} />
                License key generated. Copy it now — it cannot be viewed again after you leave this page.
              </p>
              <div className="flex items-stretch gap-2">
                <code className="block flex-1 break-all rounded bg-white px-3 py-2 text-sm text-slate-900">
                  {newLicenseKey}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(newLicenseKey);
                    setKeyCopied(true);
                  }}
                  className="shrink-0 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                >
                  {keyCopied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Ticket size={16} />
            Support Ticket Management
          </h2>
          <div className="mb-4 grid gap-2">
            <input
              value={ticketSubject}
              onChange={(e) => setTicketSubject(e.target.value)}
              placeholder="Ticket subject"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
              value={ticketDescription}
              onChange={(e) => setTicketDescription(e.target.value)}
              placeholder="Describe the client support request"
              rows={3}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void handleCreateTicket()}
              disabled={!ticketSubject || !ticketDescription}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-400"
            >
              Create Ticket
            </button>
          </div>
          <div className="space-y-2">
            {tickets.slice(0, 5).map((ticket) => (
              <div key={ticket.ticket_id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-slate-900">{ticket.subject}</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {ticket.status}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{ticket.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
          Credential Portfolio
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">
                  <span className="sr-only">Expand</span>
                </th>
                {!viewingTenantId && <th className="px-4 py-3">Tenant</th>}
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3" title="Only a partial, non-secret prefix of the license key is kept after creation. The full key is shown once when generated and cannot be retrieved again.">
                  License Key Prefix
                </th>
                <th className="px-4 py-3">Expiration</th>
                <th className="px-4 py-3">Usage</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {licenses.map((item) => {
                const isExpanded = expandedLicenseId === item.license_id;
                return (
                  <Fragment key={item.license_id}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-2 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedLicenseId(isExpanded ? null : item.license_id)
                          }
                          className="flex items-center justify-center rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          aria-expanded={isExpanded ? "true" : "false"}
                          aria-label={`${isExpanded ? "Collapse" : "Expand"} license details`}
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </td>
                      {!viewingTenantId && (
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {item.tenant_id}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{item.client_name}</p>
                        <p className="text-xs text-slate-500">{item.contact_email}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{item.role}</td>
                      <td
                        className="px-4 py-3 font-mono text-xs text-slate-500"
                        title="This is the complete key prefix — not truncated. The rest of the license key was shown once at creation and cannot be viewed again, here or anywhere."
                      >
                        {item.key_prefix}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{formatDate(item.expires_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {item.login_count} logins / {item.request_count} requests
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          item.status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1.5">
                          {item.status === "active" && (
                            <button
                              type="button"
                              onClick={() => void handleRevoke(item.license_id, item.tenant_id)}
                              className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                            >
                              <XCircle size={14} />
                              Revoke
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleReissue(item.license_id, item.tenant_id)}
                            title="Lost the key? Revoke this license and generate a brand new one."
                            className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                          >
                            <RefreshCw size={14} />
                            Reissue
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={viewingTenantId ? 7 : 8} className="px-6 py-4">
                          <div className="col-span-2 mb-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 sm:col-span-3">
                            The full license key is only ever shown once, at the moment it is
                            generated. It cannot be displayed again by anyone — not in this
                            expanded view, not by an admin, not by support. If it was lost,
                            revoke this license and generate a new one.
                          </div>
                          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs sm:grid-cols-3">
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                License Key Prefix
                              </p>
                              <p className="mt-0.5 font-mono text-slate-700">{item.key_prefix}</p>
                              <p className="mt-0.5 text-[11px] text-slate-400">
                                Complete value — the rest of the key is not recoverable.
                              </p>
                            </div>
                            <div>
                              <p
                                className="font-semibold uppercase tracking-wide text-slate-400"
                                title="Internal record ID, not the license key itself."
                              >
                                License ID
                              </p>
                              <p className="mt-0.5 font-mono text-slate-700">{item.license_id}</p>
                            </div>
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                Created
                              </p>
                              <p className="mt-0.5 text-slate-700">{formatDate(item.created_at)}</p>
                            </div>
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                Created By
                              </p>
                              <p className="mt-0.5 text-slate-700">{item.created_by || "—"}</p>
                            </div>
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                Last Login
                              </p>
                              <p className="mt-0.5 text-slate-700">
                                {item.last_login_at ? formatDate(item.last_login_at) : "Never"}
                              </p>
                            </div>
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                Last Updated
                              </p>
                              <p className="mt-0.5 text-slate-700">{formatDate(item.updated_at)}</p>
                            </div>
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                User ID
                              </p>
                              <p className="mt-0.5 font-mono text-slate-700">{item.user_id || "—"}</p>
                            </div>
                            <div className="col-span-2 sm:col-span-3">
                              <p className="font-semibold uppercase tracking-wide text-slate-400">
                                Notes
                              </p>
                              <p className="mt-0.5 whitespace-pre-wrap text-slate-700">
                                {item.notes || "No notes for this license."}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!licenses.length && (
                <tr>
                  <td colSpan={viewingTenantId ? 7 : 8} className="px-4 py-10 text-center text-slate-500">
                    No licenses yet. Generate the first client license above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Recent Login Activity</h2>
        <div className="space-y-2">
          {activity.slice(0, 10).map((item) => (
            <div key={item.activity_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <div>
                <p className="font-medium text-slate-800">
                  {item.contact_email ?? "Unknown license"} 
                  <span className="ml-2 text-xs font-normal text-slate-500">{item.reason ?? "verified"}</span>
                </p>
                <p className="text-xs text-slate-500">{formatDate(item.created_at)}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                item.success ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
              }`}>
                {item.success ? "Success" : "Failed"}
              </span>
            </div>
          ))}
          {!activity.length && <p className="text-sm text-slate-500">No login activity recorded yet.</p>}
        </div>
      </section>
    </div>
  );
}
